import { DOMUtils } from '../../../../utils/dom'
import { IAd } from '../../ad'
import { AdTarget, AdTargets } from '../../targeting/target'
import { IAdServiceAdapter } from '../adapter'

export interface IGPTAd extends IAd {
    slot: any
}

interface IWindow extends Window {
  googletag: any
}

declare let window: IWindow

export class GPTAdapter implements IAdServiceAdapter {
    public ads: IGPTAd[] = []
    public adURI: string
    public lazyLoadConf = {
        fetchMarginPercent: 500,  // 5 viewports
        mobileScaling: 2.0,  // double values for mobile
        renderMarginPercent: 200  // 2 viewports
    }

    private jsSrc = '//www.googletagservices.com/tag/js/gpt.js'

    /**
     * Initializes GPT (instantiate global object and queue).
     */
    constructor() {
        // load GPT JS
        DOMUtils.loadScript(this.jsSrc)

        // setup queue
        window.googletag = window.googletag || {}
        window.googletag.cmd = window.googletag.cmd || []

        // enqueue base setup
        window.googletag.cmd.push(() => {
            window.googletag.pubads().addEventListener('slotRenderEnded', this.slotRenderEnded.bind(this))
            window.googletag.pubads().enableLazyLoad(this.lazyLoadConf)
            window.googletag.pubads().enableSingleRequest()
        })
    }

    /**
     * Removes registered ads.
     *
     * @param ads
     */
    public destroy(ads: IAd[]) {
        window.googletag.destroySlots(ads.map((del) => this.ads.find((ad) => del.id === ad.id).slot))
        ads.forEach((del) => this.ads = this.ads.filter((_) => del.id !== _.id))
    }

    /**
     * Calls `defineSlot` for given ad.
     *
     * @param ad
     */
    public registerAd(ad: IGPTAd): void {

        // store registered ads
        this.ads.push(ad)

        // queue slot definition
        window.googletag.cmd.push(() => {
            if (ad.isOutOfPage) {
                ad.slot = window.googletag.defineOutOfPageSlot(this.adURI, ad.id)
            } else {
                ad.slot = window.googletag.defineSlot(this.adURI, ad.size, ad.id)
            }
            ad.slot.addService(window.googletag.pubads())

            // add ad-level targeting
            if (ad.targets) {
                [...ad.targets].forEach((target: AdTarget) =>
                    ad.slot.setTargeting(target.key, target.value))
            }
        })
    }

    /**
     * Calls `enableServices` to load ads, then `display` per ad.
     */
    public requestAds(): void {
        window.googletag.cmd.push(() => {
            window.googletag.enableServices()
            this.ads.forEach((ad) => window.googletag.display(ad.id))
        })
    }

    /**
     * Sets page-level targeting information.
     */
    public target(targets: AdTargets): void {
        window.googletag.cmd.push(() =>
            [...targets].map((target) =>
                window.googletag.pubads().setTargeting(target.key, target.value)))
    }

    /**
     * Handles slot load events. Calls `IAd.display` with acutal size.
     *
     * @param e event object from google
     */
    private slotRenderEnded(e: any) {
        const id = e.slot.getSlotId().getDomId()
        this.ads.find((ad) => ad.id === id).display(e.isEmpty ? undefined : e.size)
    }
}
